import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip } from '../ui/Tooltip';
import { Package, TrendingUp, DollarSign, Calculator, AlertCircle } from 'lucide-react';
import { getCurrencySymbol } from '../../constants/currencies';
import { useBusinessPlan } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Displays product performance metrics for per_unit and percentage_of_revenue costs
 * Auto-fetches product data and shows calculated cost amounts
 * 
 * @param {string} mode - "planning" for Cost Management, "actuals" for Record Actuals
 */
const ProductMetricsDisplay = ({ 
  linkedProductId, 
  costType, 
  costAmount,
  currency,
  mode = "planning", // "planning" or "actuals"
  planId = null, // Required for actuals mode
  onCalculatedAmountChange 
}) => {
  const { currentPlan } = useBusinessPlan();
  const [productMetrics, setProductMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(null);

  useEffect(() => {
    if (linkedProductId && (costType === 'per_unit' || costType === 'percentage_of_revenue')) {
      fetchProductMetrics();
    } else {
      setProductMetrics(null);
      setCalculatedCost(null);
    }
  }, [linkedProductId, costType, mode]);

  // Recalculate when cost amount changes
  useEffect(() => {
    if (productMetrics && costAmount) {
      calculateCost();
    }
  }, [productMetrics, costAmount, costType]);

  const fetchProductMetrics = async () => {
    try {
      setLoading(true);
      
      let response;
      if (mode === "planning") {
        // Fetch PLANNING data from SF Products (for Cost Management)
        response = await axios.get(
          `${API}/v2/products-enhanced/product-planning/${linkedProductId}`
        );
      } else {
        // Fetch ACTUALS data from sales actuals (for Record Actuals)
        const effectivePlanId = planId || currentPlan?.id;
        response = await axios.get(
          `${API}/actual-data/product-performance/${effectivePlanId}/${linkedProductId}`
        );
      }
      
      setProductMetrics(response.data);
      console.log('🔍 Product Metrics Fetched:', {
        mode,
        productId: linkedProductId,
        data: response.data
      });
    } catch (error) {
      console.error('Error fetching product metrics:', error);
      console.error('Error details:', error.response?.data);
      // Set default values if no data
      setProductMetrics({
        product_name: 'Unknown Product',
        [mode === "planning" ? "is_planning_data" : "has_actuals"]: false,
        ytd_units_sold: 0,
        ytd_revenue: 0,
        ytd_planned_units: 0,
        ytd_planned_revenue: 0,
        monthly_avg_units: 0,
        monthly_avg_revenue: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateCost = () => {
    if (!productMetrics || !costAmount) return;

    let calculated = 0;
    let basis = '';
    let formula = '';

    if (costType === 'per_unit') {
      // Per Unit: Cost per unit × Units
      const units = mode === "planning" 
        ? (productMetrics.ytd_planned_units || productMetrics.base_year_units || 0)
        : (productMetrics.ytd_units_sold || productMetrics.monthly_avg_units || 0);
      calculated = parseFloat(costAmount) * units;
      basis = `${units.toLocaleString()} units`;
      formula = `${getCurrencySymbol(currency)}${costAmount} per unit × ${units.toLocaleString()} units`;
    } else if (costType === 'percentage_of_revenue') {
      // Percentage of Revenue: Revenue × (Percentage / 100)
      const revenue = mode === "planning"
        ? (productMetrics.ytd_planned_revenue || productMetrics.planned_revenue_per_period || 0)
        : (productMetrics.ytd_revenue || productMetrics.monthly_avg_revenue || 0);
      calculated = revenue * (parseFloat(costAmount) / 100);
      basis = `${getCurrencySymbol(currency)}${revenue.toLocaleString()}`;
      formula = `${costAmount}% of ${getCurrencySymbol(currency)}${revenue.toLocaleString()}`;
    }

    const result = {
      amount: calculated,
      basis,
      formula
    };

    setCalculatedCost(result);
    
    // Notify parent component
    if (onCalculatedAmountChange) {
      onCalculatedAmountChange(calculated);
    }
  };

  if (!linkedProductId || (costType !== 'per_unit' && costType !== 'percentage_of_revenue')) {
    return null;
  }

  if (loading) {
    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-700">Loading product performance data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!productMetrics) {
    return null;
  }

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-base">
          <Package className="h-4 w-4 mr-2 text-blue-600" />
          {mode === "planning" ? "Product Planning Metrics" : "Product Performance Metrics"}
          <Badge variant="outline" className="ml-2 text-xs">
            {costType === 'per_unit' ? 'Per Unit Cost' : 'Revenue-Based Cost'}
          </Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          {mode === "planning" 
            ? "Planning data from Strategic Framework Products for cost estimation"
            : "Real-time data from product actuals for dynamic cost calculation"
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Product Info */}
        <div className="bg-white rounded-lg p-3 border border-blue-100">
          <p className="font-semibold text-sm text-gray-900 mb-2">
            {productMetrics.product_name || 'Product'}
          </p>
          
          {mode === "planning" && productMetrics.is_planning_data && productMetrics.base_year_units > 0 && (
            <div className="flex items-start space-x-2 text-xs text-blue-700 bg-blue-50 p-2 rounded">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                Using planned units and revenue from Strategic Framework Products page.
                This is for planning/budgeting purposes.
              </span>
            </div>
          )}
          
          {mode === "planning" && (!productMetrics.base_year_units || productMetrics.base_year_units === 0) && (
            <div className="flex items-start space-x-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                <strong>No planning data found!</strong> This product has no base_year_units or base_price set in Strategic Framework Products page. 
                Please go to SF → Products/Services and set "Base Year Units" and "Base Price" for this product to enable cost calculation.
              </span>
            </div>
          )}
          
          {mode === "actuals" && !productMetrics.has_actuals && (
            <div className="flex items-start space-x-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                No actual sales data recorded yet. Using planning estimates or zero values.
                Record sales actuals for accurate cost calculations.
              </span>
            </div>
          )}
        </div>

        {/* Performance Metrics Grid */}
        <div className="space-y-3">
          {/* Product Metrics */}
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {mode === "planning" ? "Planning Data (From SF Products)" : "Actual Performance Data"}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {costType === 'per_unit' && (
              <>
                <Tooltip content={mode === "planning" ? "Planned units from Strategic Framework Products" : "Year-to-date total units sold from sales actuals"}>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center text-xs text-gray-600 mb-1">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {mode === "planning" ? "Planned Units (YTD)" : "YTD Units Sold"}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {mode === "planning"
                        ? (productMetrics.ytd_planned_units || 0).toLocaleString()
                        : (productMetrics.ytd_units_sold || 0).toLocaleString()
                      }
                    </div>
                  </div>
                </Tooltip>

                <Tooltip content={mode === "planning" ? "Average monthly planned units" : "Average monthly units sold (YTD average)"}>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center text-xs text-gray-600 mb-1">
                      <Package className="h-3 w-3 mr-1" />
                      Monthly Avg
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {(productMetrics.monthly_avg_units || 0).toLocaleString()}
                    </div>
                  </div>
                </Tooltip>
              </>
            )}

            {costType === 'percentage_of_revenue' && (
              <>
                <Tooltip content={mode === "planning" ? "Planned revenue from Strategic Framework Products" : "Year-to-date total revenue from sales actuals"}>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center text-xs text-gray-600 mb-1">
                      <DollarSign className="h-3 w-3 mr-1" />
                      {mode === "planning" ? "Planned Revenue (YTD)" : "YTD Revenue"}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {getCurrencySymbol(currency)}
                      {mode === "planning"
                        ? (productMetrics.ytd_planned_revenue || 0).toLocaleString()
                        : (productMetrics.ytd_revenue || 0).toLocaleString()
                      }
                    </div>
                  </div>
                </Tooltip>

                <Tooltip content={mode === "planning" ? "Average monthly planned revenue" : "Average monthly revenue (YTD average)"}>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center text-xs text-gray-600 mb-1">
                      <DollarSign className="h-3 w-3 mr-1" />
                      Monthly Avg
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {getCurrencySymbol(currency)}
                      {(productMetrics.monthly_avg_revenue || 0).toLocaleString()}
                    </div>
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        {/* Calculated Cost - ALWAYS SHOW with clear messaging */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            💰 Your Cost Calculation
          </div>
          
          {calculatedCost && costAmount ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center text-sm font-medium text-green-900">
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculated Cost Amount
                </div>
                <Badge className="bg-green-600">Auto-Calculated</Badge>
              </div>
              
              <div className="text-3xl font-bold text-green-900 mb-2">
                {getCurrencySymbol(currency)}
                {calculatedCost.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              
              <div className="text-xs text-green-700 space-y-1 bg-white/50 p-2 rounded">
                <p>
                  <span className="font-semibold">Formula:</span> {calculatedCost.formula}
                </p>
                <p>
                  <span className="font-semibold">Based on:</span> {calculatedCost.basis}
                </p>
              </div>

              <div className="mt-3 text-xs text-green-800 bg-green-100/50 p-2 rounded">
                💡 <strong>{mode === "planning" ? "Planning Mode" : "Auto-Sync"}:</strong> 
                {mode === "planning" 
                  ? " This cost is calculated from planned product data in Strategic Framework. For actuals comparison, record actual costs."
                  : " This cost will automatically update when product sales actuals change."
                }
                {mode === "actuals" && costType === 'per_unit' && ' Cost scales with production volume.'}
                {mode === "actuals" && costType === 'percentage_of_revenue' && ' Cost scales with revenue performance.'}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <Calculator className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600 mb-1">
                <strong>Enter your cost rate above to see calculated amount</strong>
              </p>
              <p className="text-xs text-gray-500">
                {costType === 'per_unit' 
                  ? 'Enter cost per unit (e.g., $5) in the "Amount" field above'
                  : 'Enter percentage (e.g., 15) in the "Amount" field above'
                }
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductMetricsDisplay;
